/**
 * PAGO FORM DIALOG - Register payment for an invoice with change calculator
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Wallet } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import { useApiError } from "@/hooks/use-api-error";
import { useRegistrarPago, useMetodosPago } from "@/hooks/use-facturacion";
import { calcularVuelto } from "../utils/factura-calculations";
import type { FacturaListItem, PagoResult } from "@/hooks/use-facturacion";

const pagoSchema = z.object({
  metodo: z.string().min(1, "facturacion.pago.metodoRequired"),
  monto_recibido: z.string().min(1, "facturacion.pago.montoPositive"),
  tipo_pago: z.string().min(1),
  referencia: z.string(),
});

type FormData = z.infer<typeof pagoSchema>;

interface PagoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  factura: FacturaListItem | null;
  onSuccess: (result?: PagoResult) => void;
}

export function PagoFormDialog({ open, onOpenChange, factura, onSuccess }: PagoFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);
  const { data: metodosPagoData } = useMetodosPago();
  const metodosPago = metodosPagoData?.results ?? [];

  const pagoMutation = useRegistrarPago({
    onSuccess: (result) => onSuccess(result),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = pagoMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(pagoSchema),
    defaultValues: {
      metodo: "",
      monto_recibido: "",
      tipo_pago: "CANCELACION",
      referencia: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        metodo: "",
        monto_recibido: "",
        tipo_pago: "CANCELACION",
        referencia: "",
      });
      setError(null);
    }
  }, [open, reset]);

  const montoRecibidoRaw = watch("monto_recibido");
  const saldo = factura ? parseFloat(factura.saldo_pendiente ?? factura.total) : 0;
  const montoRecibido = parseFloat(montoRecibidoRaw) || 0;
  const { montoAplicado, vuelto, restante } = calcularVuelto(saldo, montoRecibido);
  const hasMontoInput = montoRecibidoRaw !== "" && !isNaN(parseFloat(montoRecibidoRaw));

  const onSubmit = (data: FormData) => {
    if (!factura) return;
    setError(null);
    pagoMutation.mutate({
      id: factura.id,
      data: {
        metodo: parseInt(data.metodo, 10),
        monto_recibido: parseFloat(data.monto_recibido),
        tipo_pago: data.tipo_pago as "ANTICIPO" | "ABONO" | "CANCELACION",
        referencia: data.referencia || undefined,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("facturacion.pago.title")}</DialogTitle>
          <DialogDescription>
            {factura ? t("facturacion.pago.description", { numero: factura.numero }) : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Saldo pendiente destacado */}
        {factura && (
          <div className="rounded-md bg-muted p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("facturacion.pago.totalFactura")}</span>
              <span className="font-mono font-medium">L.{factura.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">{t("facturacion.pago.saldoPendiente")}</span>
              <span className="font-mono font-bold text-orange-600">L.{saldo.toFixed(2)}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="space-y-2">
            <Label>{t("facturacion.pago.metodo")} *</Label>
            <Select
              value={watch("metodo") || ""}
              onValueChange={(val) => setValue("metodo", val, { shouldValidate: true })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("facturacion.pago.selectMetodo")} />
              </SelectTrigger>
              <SelectContent>
                {metodosPago
                  .filter((m) => m.is_active)
                  .map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>
                      {m.nombre}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.metodo && (
              <p className="text-sm text-destructive">{t(errors.metodo.message as string)}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="monto_recibido">{t("facturacion.pago.montoRecibido")} *</Label>
            <Input
              id="monto_recibido"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              {...register("monto_recibido")}
            />
            {errors.monto_recibido && (
              <p className="text-sm text-destructive">
                {t(errors.monto_recibido.message as string)}
              </p>
            )}
          </div>

          {/* Calculadora de vuelto en tiempo real */}
          {hasMontoInput && (
            <div className="rounded-md border p-3 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Wallet className="h-4 w-4" />
                <span className="font-medium">{t("facturacion.pago.resumen")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("facturacion.pago.saldoPendiente")}
                </span>
                <span className="font-mono">L.{saldo.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("facturacion.pago.montoRecibido")}</span>
                <span className="font-mono">L.{montoRecibido.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("facturacion.pago.montoAplicado")}</span>
                <span className="font-mono">L.{montoAplicado.toFixed(2)}</span>
              </div>
              <Separator />
              {vuelto > 0 ? (
                <div className="flex justify-between font-semibold text-green-600 dark:text-green-400">
                  <span>{t("facturacion.pago.vuelto")}</span>
                  <span className="font-mono">L.{vuelto.toFixed(2)}</span>
                </div>
              ) : restante > 0 ? (
                <div className="flex justify-between font-semibold text-orange-600">
                  <span>{t("facturacion.pago.restante")}</span>
                  <span className="font-mono">L.{restante.toFixed(2)}</span>
                </div>
              ) : (
                <div className="flex justify-between font-semibold text-green-600 dark:text-green-400">
                  <span>{t("facturacion.pago.pagoExacto")}</span>
                  <span className="font-mono">L.0.00</span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("facturacion.pago.tipoPago")}</Label>
            <Select
              value={watch("tipo_pago")}
              onValueChange={(val) => setValue("tipo_pago", val, { shouldValidate: true })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CANCELACION">
                  {t("facturacion.pago.tipos.cancelacion")}
                </SelectItem>
                <SelectItem value="ABONO">{t("facturacion.pago.tipos.abono")}</SelectItem>
                <SelectItem value="ANTICIPO">{t("facturacion.pago.tipos.anticipo")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="referencia">{t("facturacion.pago.referencia")}</Label>
            <Input
              id="referencia"
              placeholder={t("facturacion.pago.referenciaPlaceholder")}
              {...register("referencia")}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("facturacion.pago.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
